import { SolutionPage } from "@/components/shared/SolutionPage";
import { ReviewsMockup } from "@/components/shared/ReviewsMockup";
import { Star, MessageSquare, Network, TrendingUp } from 'lucide-react';
import { Link } from "react-router-dom";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

/**
 * Capability spoke: "Reputation & Review Management".
 * Single-intent page for "reputation management" / "get more google reviews" /
 * "review management" demand. Hub is /platform; clustered to its parent layers
 * /solutions/crm-automation (the engine that requests) + /solutions/web-design-local-seo
 * (reviews power the local pack). Outcome-framed, no invented metrics. Compliant:
 * asks every customer, never gates negative reviews (Google policy + FTC).
 */
const Reputation = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Reputation Management"
      seo={{
        title: "Reputation & Review Management for Local Business | Frayze",
        description: "Frayze asks every happy customer for a review automatically, alerts you to the unhappy ones, and replies to all of them, so your Google rating climbs and buyers pick you first.",
        keywords: "reputation management, review management software, get more google reviews, automated review requests, online review generation, respond to reviews, google business profile reviews",
        canonical: "https://frayze.ca/platform/reputation-management/"
      }}
      hero={{
        badge: "Capability: Reputation",
        badgeColor: "text-blue-700",
        title: "Turn Every Happy Customer Into a 5-Star Review.",
        description: "Frayze asks for the review at the moment they're happiest, makes leaving one a two-tap job, and replies to every rating that lands, so your Google profile fills with recent 5-star reviews while competitors sit on stale ones.",
        buttonColor: "bg-blue-700 hover:bg-blue-800",
        gradientParams: "from-blue-500/5 via-background to-background",
        primaryCta: { text: "Build My Reputation", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "See Pricing", link: "/pricing/" },
        visual: <ReviewsMockup />
      }}
      problem={{
        title: "Buyers Read Your Reviews Before They Ever Call You",
        description: (
          <>
            The map pack is a beauty contest decided by stars and recency. A handful of old four-star reviews loses to the competitor with two hundred recent ones, and you never even make the shortlist.
            <br /><br />
            <strong>The industry research is clear:</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "9 in 10", label: "Buyers read online reviews before choosing a local business" },
          { value: "88%", label: "Trust online reviews as much as a personal recommendation (industry research)" },
          { value: "First", label: "Your star rating is the first thing a shopper sees in the map pack, before your website" }
        ]
      }}
      systemModel={{
        title: "How the Review Engine Works",
        description: "Every finished job becomes a review request: at the moment the customer is happiest.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-blue-500/20 via-blue-500/50 to-blue-500/20",
        items: [
          { step: "1", title: "Job Done", description: "A sale closes or a job wraps. The system knows and kicks off the ask automatically." },
          { step: "2", title: "Perfectly-Timed Ask", description: "The customer gets a friendly, branded text and email with a one-tap link to leave a review." },
          { step: "3", title: "One Tap to Post", description: "Leaving a review takes seconds. The same message also gives an unhappy customer a direct line to your team, so problems reach you fast instead of getting buried online." },
          { step: "4", title: "Every Review Answered", description: "AI drafts an on-brand reply to each review. You approve, it posts. Consistent responses signal a business that cares." }
        ]
      }}
      layers={{
        title: "What Comes With the Review Engine",
        description: "Requests, monitoring, and replies. Run for you across every site that matters, not one more dashboard to check.",
        theme: "blue",
        items: [
          {
            title: "The Review Engine",
            icon: Star,
            items: [
              "Auto-requests after every job or sale",
              "One-tap Google & Facebook review links",
              "Branded text + email, timed to land"
            ]
          },
          {
            title: "Monitor & Respond",
            icon: MessageSquare,
            items: [
              "Every review in one unified inbox",
              "AI-drafted, on-brand replies you approve",
              "Live review widget embedded on your site"
            ]
          }
        ]
      }}
      interdependence={{
        title: "Reputation Feeds the Whole Machine",
        description: (
          <>
            Reviews aren't a vanity metric: they're the fuel for local search and trust. They power your <Link to="/solutions/local-seo-reputation/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">Google Business Profile and local-pack ranking</Link>, get requested automatically by the <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM after every job</Link>, and the <Link to="/platform/" className="font-bold underline decoration-purple-500/50 hover:text-purple-600 transition-colors">AI drafts every reply</Link>.
            <br /><br />
            See where it sits in the wider <Link to="/platform/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">managed platform</Link>.
          </>
        ),
        icon: Network,
        quote: "Stars win the click. The system earns the stars."
      }}
      useCases={{
        title: "Where Reputation Wins the Most",
        items: [
          { title: "Car Dealerships", description: "A dealer with hundreds of recent five-star reviews beats the lot down the road every time a shopper checks Google.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "Homeowners hire the roofer or plumber with the most recent five-star reviews: reputation is the whole pitch.", link: "/industries/trades/" },
          { title: "Any Local, High-Trust Business", description: "Clinics, salons, firms. When people choose who to trust, your review wall does the selling.", link: "/solutions/local-seo-reputation/" }
        ]
      }}
      socialProof={{
        title: "Built by an operator who ran multi-rooftop reputations",
        quote: "Before Frayze, Denis ran the systems that kept six dealership rooftops answering customers and earning reviews at scale. That same 'ask every time, reply every time' discipline is what Frayze installs for you.",
        icon: TrendingUp,
        ctaText: "See the Need A Car story",
        ctaLink: "/case-studies/need-a-car-automotive-growth/"
      }}
      cta={{
        title: "Own the Best Reputation in Your Market",
        description: "Turn on the review engine and fill your Google profile with recent, five-star reviews automatically.",
        primaryCta: { text: "Build My Reputation", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "Get Your Free Reputation Audit", link: "/digital-audit/" },
        color: "bg-blue-700 hover:bg-blue-800"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          <ServiceFAQ
            title="Reputation & Review Management: Common Questions"
            description="Straight answers on how the review engine gets you more reviews and handles the replies."
            items={[
              {
                question: "How does Frayze get me more reviews?",
                answer: "Every time you finish a job or close a sale, the system automatically texts and emails the customer a one-tap link to leave a review. Asking every customer, at the moment they are happiest, is what turns a trickle of reviews into a steady stream."
              },
              {
                question: "Is this allowed under Google's review policies?",
                answer: "Yes. Frayze asks every customer for honest feedback and never hides the review option from anyone. That keeps you onside with Google's policies, which prohibit selectively soliciting only positive reviews. You get more reviews because you ask consistently, not because you filter who can leave one."
              },
              {
                question: "What happens with a negative review?",
                answer: "You are alerted the moment it lands so you can respond fast and professionally. A calm, helpful reply to a critic often reads better to future customers than a wall of perfect scores, and the system can draft that response for you to approve."
              },
              {
                question: "Do the review requests come from my business?",
                answer: "Yes. The text and email are branded with your business name, so customers recognize who is asking. New reviews and replies all surface in one inbox your team manages from desktop or the mobile app."
              },
              {
                question: "Which review sites does it cover?",
                answer: "Google is the priority because it drives local search, but the engine can route to Facebook and industry sites too, and embed a live review widget on your website so visitors see the proof without leaving your page."
              },
              {
                question: "Can AI really write the replies?",
                answer: "It drafts them (on-brand and specific to what the reviewer said) and you approve before anything posts. You can also let it auto-reply to straightforward five-star reviews and hold anything sensitive for a human."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default Reputation;
